import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons';
import { useUiT } from '../i18n';
import { cx } from './cx';

// Kbd, Skeleton and EmptyState live in their own modules (ui-primitives).

export function Spinner({ size = 16, label }: { size?: number; label?: string }) {
  const t = useUiT();
  return (
    <span
      className="spinner"
      role="status"
      aria-label={label ?? t('ui.loading')}
      style={{ width: size, height: size }}
    />
  );
}

export function ProgressBar({ value, label, color }: { value: number; label?: string; color?: string }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <span
      className="progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={label}
    >
      <span style={{ width: `${pct}%`, ...(color ? { background: color } : {}) }} />
    </span>
  );
}

export function Callout({
  tone = 'info',
  icon,
  title,
  children,
  action,
}: {
  tone?: 'info' | 'warn' | 'err' | 'neutral';
  icon?: IconName;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const defaultIcon: IconName = tone === 'warn' ? 'alert' : tone === 'err' ? 'alert' : 'info';
  return (
    <div
      className={cx('callout', tone !== 'neutral' && tone)}
      role={tone === 'err' || tone === 'warn' ? 'alert' : 'status'}
    >
      <Icon name={icon ?? defaultIcon} size={16} style={{ marginTop: 1 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
        {title ? <span style={{ fontWeight: 600, fontSize: 'var(--text-13)' }}>{title}</span> : null}
        {children ? <span>{children}</span> : null}
      </span>
      {action}
    </div>
  );
}
